import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { tenantApiFetch } from './tenantApi';

export default function TenantApiKeyCreatePage() {
  const navigate = useNavigate();
  const [keyName, setKeyName] = useState('');
  const [scopesText, setScopesText] = useState('read');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [secretDialog, setSecretDialog] = useState(null);

  const parseScopes = () =>
    scopesText
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        key_name: keyName.trim(),
        scopes: parseScopes()
      };
      if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      const res = await tenantApiFetch('/api/tenant/api-keys', { method: 'POST', body });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Create failed');
      setSecretDialog({
        secret: json.secretShownOnce,
        id: json.apiKey?.id
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!secretDialog?.secret) return;
    try {
      await navigator.clipboard.writeText(secretDialog.secret);
    } catch (_e) {
      /* ignore */
    }
  };

  const closeSecretAndGo = () => {
    const id = secretDialog?.id;
    setSecretDialog(null);
    if (id) navigate(`/tenant/api-keys/${id}`);
    else navigate('/tenant/api-keys');
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Create API key
      </Typography>
      <Button sx={{ mb: 2 }} onClick={() => navigate('/tenant/api-keys')}>
        Back to list
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 560 }}>
        <TextField
          label="Key name"
          fullWidth
          required
          margin="normal"
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
          helperText="A label for your team (not secret)."
        />
        <TextField
          label="Scopes"
          fullWidth
          margin="normal"
          value={scopesText}
          onChange={(e) => setScopesText(e.target.value)}
          helperText="Space or comma separated (e.g. read proctoring.write)."
        />
        <TextField
          label="Expires at (optional)"
          type="datetime-local"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            disabled={busy || !keyName.trim()}
            onClick={submit}
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Create
          </Button>
        </Box>
      </Paper>

      <Dialog open={Boolean(secretDialog)} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>Save your API key now</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This secret is shown only once. Copy it to a password manager. It cannot be retrieved later.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={secretDialog?.secret || ''}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={copySecret}>
            Copy
          </Button>
          <Button variant="contained" onClick={closeSecretAndGo}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
